/**
 * Loader / splash branding for the public tour viewer.
 *
 * Prefer the tour's brand profile when available; fall back to 360Worlds
 * tokens on the consumer host (or for curated worlds tours), otherwise the
 * default 360Tours Studio orange.
 */
import { isWorldsHost } from './worldsHost';

export const WORLDS_LOADER_BRAND = {
  name: '360Worlds',
  /** Full wordmark — used on full-page splash / chunk loaders. */
  logoUrl: '/360worlds-logo.png',
  /** Compact planet mark — fits inside the PSV circular loader. */
  markUrl: '/360worlds-favicon.png',
  accent: '#06b6d4',
  accentSecondary: '#8b5cf6',
} as const;

export const TOURS_LOADER_BRAND = {
  name: '360 Tours',
  logoUrl: '/360tours-studio-logo-transparent.png',
  markUrl: '/360tours-icon.png',
  accent: '#f97316',
  accentSecondary: '#fb923c',
} as const;

export interface LoaderBranding {
  name: string;
  /** Full logo for splash screens. */
  logoUrl: string;
  /** Compact mark for the circular panorama loader. */
  markUrl: string;
  accent: string;
  accentSecondary: string;
  /** True when resolved to 360Worlds tokens (host or worlds tour). */
  isWorlds: boolean;
}

export interface ResolveLoaderBrandingOptions {
  /** Tour is in the curated 360Worlds collection. */
  isWorldsTour?: boolean;
  brandLogoUrl?: string | null;
  brandName?: string | null;
  brandAccent?: string | null;
}

/** Resolve logo + accent for tour loaders from brand / host / defaults. */
export function resolveLoaderBranding(opts: ResolveLoaderBrandingOptions = {}): LoaderBranding {
  const isWorlds = !!opts.isWorldsTour || isWorldsHost();
  const base = isWorlds ? WORLDS_LOADER_BRAND : TOURS_LOADER_BRAND;
  const brandLogo = opts.brandLogoUrl?.trim() || null;
  const brandAccent = opts.brandAccent?.trim() || null;

  return {
    name: opts.brandName?.trim() || base.name,
    logoUrl: brandLogo || base.logoUrl,
    // Prefer a dedicated mark for the circle; fall back to brand logo if no
    // platform mark applies (custom brand without a square icon).
    markUrl: isWorlds || !brandLogo ? base.markUrl : brandLogo,
    accent: brandAccent || base.accent,
    accentSecondary: isWorlds ? WORLDS_LOADER_BRAND.accentSecondary : base.accentSecondary,
    isWorlds,
  };
}
